import React, { useCallback, useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Box, useTheme } from '@mui/material'
import { toast } from 'react-toastify'

import { exportQuestionBanksAuditReport } from '../../../utils/auditing-export-utilities'
import DataLoadingSpinner from '../../common/DataLoadingSpinner'
import TryAgainMessageBlock from '../../common/TryAgainMessageBlock'
import AuditPageStatsFilterBar from '../AuditPageStatsFilterBar'
import CustomTable from '../../common/custom-table/CustomTable'
import { Icons } from '../../../utils/utilities'
import api from '../../../service/api'



const columns = [
    { id: 1, field: "index", index: true, name: 'Sl. No.', width: "80px", align: "center", padding: "10px 20px" },
    { id: 2, field: "faculty_name", filterable: true, sortable: true, linkGetter: row => `/${window.location.pathname?.includes( 'admin' ) ? "admin" : "audit"}/faculty/${row.created_by}?legacy=${row.legacy_system_data}`, name: "Faculty", cellStyles: { textTransform: "capitalize" }, width: "150px", padding: "10px 20px" },
    { id: 3, field: "created_by", filterable: true, linkGetter: row => `/${window.location.pathname?.includes( 'admin' ) ? "admin" : "audit"}/faculty/${row.created_by}?legacy=${row.legacy_system_data}`, name: 'Empcode', align: "left", padding: "10px 20px" },
    { id: 6, field: "department", filterable: true, renderCell: row => `${row.institute} - ${row.department?.toLowerCase()}`, cellStyles: { textTransform: "capitalize" }, name: "Department", width: "150px", padding: "10px 20px" },
    { id: 4, field: "count", filterable: true, sortable: true, align: "center", name: "Question banks", width: "150px", padding: "10px 20px" },
    { id: 5, field: "questions", filterable: true, sortable: true, align: "center", name: "Questions", width: "150px", padding: "10px 20px" },
]

const QuestionBanksStats = () => {

    const [searchParams] = useSearchParams( { searchText: "", month: "", selectedDepartment: "all", selectedInstitute: "all", view: "tabular", sortByName: "0", sortByDept: "0", sortByQuestionsCount: "0", sortByQuestionBanksCount: "0" } )
    const month = searchParams.get( 'month' ) || ""
    const view = searchParams.get( 'view' ) || "tabular"
    const selectedInstitute = searchParams.get( 'selectedInstitute' ) || "all"
    const selectedDepartment = searchParams.get( 'selectedDepartment' ) || "all"

    const [stats, setStats] = useState( [] )
    const [loading, setLoading] = useState( true )
    const [statsError, setStatsError] = useState( null )
    const [facultyStats, setFacultyStats] = useState( [] )
    const [last12MonthsStats, setLast12MonthsStats] = useState( null )
    const [loadingZeroQuestionBanksFaculties, setLoadingZeroQuestionBanksFaculties] = useState( false )
    const [showFacultiesWithZeroQuestionBanks, setShowFacultiesWithZeroQuestionBanks] = useState( false )

    const { layout } = useTheme()


    const filterFunc = item => {
        if ( selectedDepartment !== "all" ) {
            const [department_id, legacy_department_id] = selectedDepartment?.split( "-" )
            return ( item.legacy_system_data && +legacy_department_id === +item?.department_id ) || ( !item.legacy_system_data && +department_id === +item?.department_id )
        }
        return true
    }

    const handleShowZeroQuestionBanksFacultiesChange = async e => {
        if ( e.target.checked ) {
            setLoadingZeroQuestionBanksFaculties( true )
            try {
                const queries = {
                    institute: selectedInstitute !== "all" ? selectedInstitute : "",
                    department: selectedDepartment !== "all" ? selectedDepartment : ""
                }
                const { data: { data: facultiesList } } = await api.audit.fetchFacultiesUnderAuditor( queries )
                const facultiesIdWithExams = facultyStats.map( i => i.created_by )
                const facultiesIdWithoutExams = facultiesList.filter( i => !facultiesIdWithExams.includes( i.session_id ) ).map( i => {
                    return {
                        count: 0,
                        questions: 0,
                        faculty_name: i.employee_name,
                        department: i.branch_name,
                        department_id: i.branch_id,
                        institute: i.institute_name_short,
                        created_by: i.session_id,
                    }
                } )
                setStats( [...facultiesIdWithoutExams, ...facultyStats] )
                setShowFacultiesWithZeroQuestionBanks( true )
            } catch ( err ) {
                console.log( err )
                toast( "Oops! Something went wrong while fetching faculties with no exams." )
            }
        } else {
            setStats( facultyStats )
            setShowFacultiesWithZeroQuestionBanks( false )
        }
        setLoadingZeroQuestionBanksFaculties( false )
    }

    const getData = useCallback( async () => {
        try {
            setLoading( true )
            const { data: { data: { facultyStats, last12MonthsStats = {} } } } = await api.audit.getQuestionBanksStats( { month, showzeroassignmentfaculties: false, institute: selectedInstitute !== "all" ? selectedInstitute : "" } )

            setFacultyStats( facultyStats )
            setStats( facultyStats )
            setLast12MonthsStats( Object.keys( last12MonthsStats ).length > 0 ? last12MonthsStats : null )
            setStatsError( null )
        } catch ( err ) {
            setStatsError( { code: err?.response?.status, message: err?.response?.data?.message || "Something went wrong while fetching the question banks audit data." } )
        } finally {
            setLoading( false )
        }
    }, [month, selectedInstitute] )

    useEffect( () => {
        getData()
    }, [getData] )

    useEffect( () => {
        setLoadingZeroQuestionBanksFaculties( false )
    }, [month] )

    const filtered = stats.filter( filterFunc )

    return (
        <Box sx={{ flexShrink: 0, display: "flex", flexDirection: "column", gap: layout.cardsGap }}>

            {/* Filter */}
            <AuditPageStatsFilterBar viewToggle={false} handleShowFacultyWithZeroResourceChange={handleShowZeroQuestionBanksFacultiesChange} showzeroassignmentsfaculties={showFacultiesWithZeroQuestionBanks} loadingZeroQuizzesFaculties={loadingZeroQuestionBanksFaculties} resource="question banks" listResourceBtnLabel="View Question Banks" title="Question banks statistics" />

            {/* Table */}
            {!statsError && !loading && view === 'tabular' && <CustomTable
                globalSearch="Search by faculty name or employee code..."
                hasFilters={selectedDepartment !== "all" || selectedInstitute !== "all"}
                pageSize={50}
                columns={columns}
                data={filtered}
                tableStyles={{
                    maxHeight: "450px"
                }}
                customActions={[
                    {
                        type: "button",
                        label: "Export",
                        startIcon: Icons.default.Publish,
                        onClick: ( rows ) => exportQuestionBanksAuditReport( rows, last12MonthsStats, `${month || "complete"}-${selectedInstitute !== "all" ? selectedInstitute + "-" : ""}${selectedDepartment !== "all" ? selectedDepartment : "alldept"}-question-banks-audit-report` )
                    }
                ]}
            />}

            {/* Retry */}
            {statsError && <TryAgainMessageBlock err={statsError.message} loading={loading} getData={getData} code={statsError.code} />}

            {/* Loading */}
            {loading && !statsError && <DataLoadingSpinner padding="0" waitingMessage="Loading question banks audit report please..." />}
        </Box >
    )
}

export default QuestionBanksStats
